/* ========================================================================
   PLP 専用の補助挙動
   適用型フィルター、並び替え、カテゴリ開閉を制御する。
   ======================================================================== */
(() => {
  const AuremePlpUtils = window.AuremePlpUtils;
  if (!AuremePlpUtils) return;

  const FILTER_OPEN_CLASS = 'aureme-plp-filter-open';
  const {
    ROOT_SELECTOR,
    buildFormSearchParams,
    createSearchParams,
    focusQueuedElementAfterRender,
    getFilterDrawerPanel,
    getFilterForm,
    getFilterTrigger,
    getNamedFieldNames,
    getRootForElement,
    getRoots,
    isElementTarget,
    queueFocusAfterRender,
    replaceSearchParamFields,
    requestFacetRender,
    syncFilterOptionStates,
  } = AuremePlpUtils;
  function closeAllSortDropdowns(exceptDetails) {
    for (const details of document.querySelectorAll('.aureme-plp-sort__details[open]')) {
      if (details === exceptDetails) continue;
      details.removeAttribute('open');
    }
  }

  function closeSortDetails(details) {
    details.removeAttribute('open');
    details.querySelector('summary')?.focus();
  }

  function closeSortDropdown(input) {
    const details = input?.closest('.aureme-plp-sort__details');
    if (!details) return;

    window.requestAnimationFrame(() => {
      closeSortDetails(details);
    });
  }

  function summarizePriceGroup(group) {
    /* price_range は常に [min, max] の順で number input が並ぶ前提 */
    const inputs = group.querySelectorAll('input[type="number"]');
    const minValue = inputs[0]?.value.trim() || '';
    const maxValue = inputs[1]?.value.trim() || '';

    if (!minValue && !maxValue) return '指定なし';
    if (minValue && maxValue) return `${minValue} - ${maxValue}`;
    if (minValue) return `${minValue} 以上`;
    return `${maxValue} 以下`;
  }

  function summarizeChoiceGroup(group) {
    const checkedInputs = Array.from(group.querySelectorAll('input:checked'));
    if (!checkedInputs.length) return '指定なし';

    const labels = checkedInputs.map((input) => input.dataset.filterLabel?.trim()).filter(Boolean);

    if (!labels.length) return '指定なし';
    return labels.join(' / ');
  }

  function getGroupSummaryText(group) {
    return group.dataset.filterType === 'price'
      ? summarizePriceGroup(group)
      : summarizeChoiceGroup(group);
  }

  function updateGroupSummary(group) {
    const summary = group.querySelector('[data-aureme-filter-selected-summary]');
    if (!summary) return;

    summary.textContent = getGroupSummaryText(group);
  }

  function syncAllGroupSummaries(scope = document) {
    const root = scope instanceof Element || scope instanceof Document ? scope : document;
    for (const group of root.querySelectorAll('[data-filter-group]')) {
      updateGroupSummary(group);
    }
  }

  function syncFilterFormState(scope = document) {
    syncFilterOptionStates(scope);
    syncAllGroupSummaries(scope);
  }

  function openFilterDrawer(root) {
    if (!root) return;

    const drawerPanel = getFilterDrawerPanel(root);

    root.classList.add('is-filter-open');
    document.body.classList.add(FILTER_OPEN_CLASS);
    getFilterTrigger(root)?.setAttribute('aria-expanded', 'true');

    /* ダイアログ外へフォーカスが抜けないよう、開いた直後にドロワー内へ閉じ込める */
    if (
      drawerPanel &&
      typeof trapFocus === 'function' &&
      typeof getFocusableElements === 'function'
    ) {
      const initialFocusTarget = getFocusableElements(drawerPanel)[0];
      if (initialFocusTarget) {
        trapFocus(drawerPanel, initialFocusTarget);
      }
    }
  }

  function closeFilterDrawer(root, { returnFocus = false } = {}) {
    if (!root) return;

    const filterTrigger = getFilterTrigger(root);

    root.classList.remove('is-filter-open');
    document.body.classList.remove(FILTER_OPEN_CLASS);
    filterTrigger?.setAttribute('aria-expanded', 'false');

    /* 既存の focus trap を解除し、必要な場合だけ起点のトリガーへフォーカスを戻す */
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(returnFocus ? filterTrigger : null);
      return;
    }

    if (returnFocus) {
      filterTrigger?.focus();
    }
  }

  function resetFilterForm(root) {
    const form = getFilterForm(root);
    if (!form) return;

    for (const input of form.querySelectorAll('input')) {
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = input.dataset.resetState === 'checked';
        continue;
      }

      input.value = input.dataset.resetValue || '';
    }

    syncFilterFormState(form);
  }

  function applyFilterForm(root) {
    const form = getFilterForm(root);
    if (!form) return;

    const searchParams = createSearchParams(window.location.search);
    /* visible form の draft 状態だけを適用ボタン押下時に URL へ反映し、途中変更では一覧を更新しない */
    replaceSearchParamFields(searchParams, getNamedFieldNames(form), buildFormSearchParams(form));
    searchParams.delete('page');

    /* 再描画後も操作起点が消えないよう、閉じた後のトリガーへフォーカスを戻す */
    queueFocusAfterRender('[data-aureme-filter-trigger]');
    closeFilterDrawer(root);
    requestFacetRender(searchParams, root);
  }

  /* アコーディオン共通トグル: 親コンテナの is-open と body の hidden を切り替える */
  function setCollapsibleState(container, body, trigger, shouldOpen) {
    container.classList.toggle('is-open', shouldOpen);
    body.hidden = !shouldOpen;
    trigger.setAttribute('aria-expanded', String(shouldOpen));
  }

  function toggleCollapsible(trigger, containerSelector, bodySelector) {
    const container = trigger.closest(containerSelector);
    if (!container) return;

    const body = container.querySelector(bodySelector);
    if (!body) return;

    const willOpen = !container.classList.contains('is-open');
    setCollapsibleState(container, body, trigger, willOpen);
  }

  function toggleAccordion(trigger) {
    toggleCollapsible(trigger, '[data-filter-group]', '.aureme-plp-filter-group__body');
  }

  function toggleCategoryParent(trigger) {
    toggleCollapsible(
      trigger,
      '[data-aureme-category-parent]',
      '.aureme-plp-filter-category-tree__parent-body',
    );
  }

  function applySort(input) {
    const root = getRootForElement(input);
    const searchParams = createSearchParams(window.location.search);

    if (input.value) {
      searchParams.set('sort_by', input.value);
    } else {
      searchParams.delete('sort_by');
    }

    searchParams.delete('page');

    closeSortDropdown(input);
    requestFacetRender(searchParams, root);
  }

  function syncRootState(scope = document) {
    const roots = getRoots(scope);

    for (const root of roots) {
      syncFilterFormState(root);
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!isElementTarget(target)) return;

    closeAllSortDropdowns(target.closest('.aureme-plp-sort__details'));

    const filterTrigger = target.closest('[data-aureme-filter-trigger]');
    if (filterTrigger) {
      event.preventDefault();
      openFilterDrawer(getRootForElement(filterTrigger));
      return;
    }

    const filterClose = target.closest('[data-aureme-plp-filter-close]');
    if (filterClose) {
      event.preventDefault();
      closeFilterDrawer(getRootForElement(filterClose), { returnFocus: true });
      return;
    }

    const filterApply = target.closest('[data-aureme-filter-apply]');
    if (filterApply) {
      event.preventDefault();
      applyFilterForm(getRootForElement(filterApply));
      return;
    }

    const filterReset = target.closest('[data-aureme-filter-reset]');
    if (filterReset) {
      event.preventDefault();
      resetFilterForm(getRootForElement(filterReset));
      return;
    }

    const accordionTrigger = target.closest('[data-aureme-filter-accordion]');
    if (accordionTrigger) {
      event.preventDefault();
      toggleAccordion(accordionTrigger);
      return;
    }

    const categoryParentTrigger = target.closest('[data-aureme-category-parent-toggle]');
    if (categoryParentTrigger) {
      event.preventDefault();
      toggleCategoryParent(categoryParentTrigger);
      return;
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!isElementTarget(target)) return;

    if (target.matches('[data-aureme-sort-input]')) {
      applySort(target);
      return;
    }

    const filterForm = target.closest('[data-aureme-plp-filter-form]');
    if (!filterForm) return;

    syncFilterFormState(filterForm);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const openRoot = document.querySelector(`${ROOT_SELECTOR}.is-filter-open`);
    if (openRoot) {
      closeFilterDrawer(openRoot, { returnFocus: true });
      return;
    }

    closeAllSortDropdowns(null);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncRootState);
  } else {
    syncRootState();
  }

  document.addEventListener('aureme:plp:rendered', () => {
    syncRootState();
    if (
      document.querySelector(`${ROOT_SELECTOR}[data-aureme-new-arrival-virtual-pagination="true"]`)
    ) {
      return;
    }

    focusQueuedElementAfterRender();
  });
})();
